import { useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { buildConversationFromImport, parseWhatsAppExport } from "../chatImport";
import { Icon } from "../icons";
import type { Conversation, ImportDraft, ImportedChatPreview } from "../types";

const EMPTY_DRAFT: ImportDraft = {
  company: "PGR Seguridad",
  customer: "",
  site: "",
  contactName: "",
  phone: "",
  assignedTo: "Mario Pérez-García",
  myParticipantName: "",
};

export function ImportStudio({
  onImported,
  onHistoryImported,
  existingGroupConversations = [],
  organizationName,
  remote,
}: {
  onImported: (conversation: Conversation) => Promise<void> | void;
  onHistoryImported?: (target: Conversation, history: Conversation, fileName: string) => Promise<void> | void;
  existingGroupConversations?: Conversation[];
  organizationName?: string;
  remote: boolean;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [sourceText, setSourceText] = useState("");
  const [preview, setPreview] = useState<ImportedChatPreview | null>(null);
  const [draft, setDraft] = useState<ImportDraft>(EMPTY_DRAFT);
  const [mode, setMode] = useState<"new" | "merge">("new");
  const [targetConversationId, setTargetConversationId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!remote || !organizationName) return;
    setDraft((current) => ({ ...current, company: organizationName }));
  }, [organizationName, remote]);

  const participantOptions = useMemo(() => preview?.participants ?? [], [preview]);
  const sampleMessages = useMemo(() => preview?.messages.slice(0, 5) ?? [], [preview]);
  const targetConversation = existingGroupConversations.find((item) => item.id === targetConversationId);
  const validTimestampCount = preview?.messages.filter((item) => item.sentAtIso).length ?? 0;

  async function readFile(file: File) {
    if (!file.name.toLowerCase().endsWith(".txt")) {
      setError("Selecciona el archivo .txt incluido en la exportación de WhatsApp.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError("El archivo supera 8 MB. Exporta un período más corto o divide la conversación.");
      return;
    }
    const text = await file.text();
    setSourceText(text);
    analyze(text, file.name);
  }

  function analyze(text = sourceText, fileName = "chat-pegado.txt") {
    setError(null);
    const parsed = parseWhatsAppExport(text, fileName);
    setPreview(parsed);
    const external = parsed.participants.find((name) => !/mario|pgr|seguridad smart/i.test(name));
    const mine = parsed.participants.find((name) => /mario|pgr|seguridad smart/i.test(name));
    setDraft((current) => ({
      ...current,
      customer: current.customer || external || "",
      contactName: current.contactName || external || "",
      myParticipantName: current.myParticipantName || mine || parsed.participants[0] || "",
    }));
    if (!parsed.messages.length) setError(parsed.warnings[0] ?? "No se detectaron mensajes.");
  }

  function handleFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file) void readFile(file);
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) void readFile(file);
  }

  function chooseMode(nextMode: "new" | "merge") {
    setMode(nextMode);
    setError(null);
    if (nextMode === "merge" && !targetConversationId && existingGroupConversations[0]) {
      setTargetConversationId(existingGroupConversations[0].id);
    }
  }

  async function finishImport() {
    if (!preview?.messages.length) return;
    if (!draft.myParticipantName.trim()) {
      setError("Selecciona cuál participante corresponde a tu equipo antes de importar.");
      return;
    }
    if (mode === "new" && (!draft.customer.trim() || !draft.site.trim())) {
      setError("Completa cliente e instalación antes de crear la conversación.");
      return;
    }
    if (mode === "merge" && (!remote || !targetConversation || !onHistoryImported)) {
      setError("Selecciona un grupo vivo de esta organización para fusionar el historial.");
      return;
    }
    if (mode === "merge" && validTimestampCount !== preview.messages.length) {
      setError("Hay mensajes sin fecha válida. Corrige o divide la exportación antes de fusionarla con el grupo vivo.");
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const effectiveDraft = mode === "merge" && targetConversation
        ? {
            company: targetConversation.company,
            customer: targetConversation.customer,
            site: targetConversation.site,
            contactName: targetConversation.contactName,
            phone: targetConversation.phone,
            assignedTo: targetConversation.assignedTo,
            myParticipantName: draft.myParticipantName,
          }
        : draft;
      const conversation = buildConversationFromImport(preview, effectiveDraft);
      if (mode === "merge" && targetConversation && onHistoryImported) {
        await onHistoryImported(targetConversation, conversation, preview.fileName);
      } else {
        await onImported(conversation);
      }
      setSourceText("");
      setPreview(null);
      setDraft(EMPTY_DRAFT);
      if (fileInput.current) fileInput.current.value = "";
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo importar la conversación.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="import-page">
      <section className="page-hero import-hero">
        <div className="page-hero-copy">
          <p className="section-kicker">HISTORIAL AUTORIZADO</p>
          <h1>Convierte una exportación de WhatsApp en memoria operativa.</h1>
          <p>
            Crea una conversación histórica independiente o fusiona mensajes anteriores con un grupo que ya está siendo capturado por Conversa Smart.
          </p>
          <div className="hero-badges">
            <span><Icon name="check" size={16} /> Deduplicación automática</span>
            <span><Icon name="check" size={16} /> Revisión humana</span>
            <span><Icon name="check" size={16} /> Evidencia vinculada</span>
          </div>
        </div>
        <div className="journey-card">
          <div className="journey-line" />
          {[
            ["01", "Importa", "Archivo .txt de WhatsApp"],
            ["02", "Vincula", "Nuevo caso o grupo existente"],
            ["03", "Analiza", remote ? "Guarda y reanaliza en AWS" : "Guarda en esta demostración"],
          ].map(([number, title, text]) => (
            <article key={number}><b>{number}</b><div><strong>{title}</strong><span>{text}</span></div></article>
          ))}
        </div>
      </section>

      <section className="import-grid">
        <div className="import-source-card card-panel">
          <div className="panel-title-row">
            <div><p className="section-kicker">PASO 1</p><h2>Carga la conversación</h2></div>
            {preview ? <span className="success-chip"><Icon name="check" size={14} /> Analizada</span> : null}
          </div>
          <div
            className={`drop-zone ${dragging ? "dragging" : ""}`}
            onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
          >
            <span className="drop-icon"><Icon name="upload" size={24} /></span>
            <strong>Arrastra aquí el archivo .txt</strong>
            <p>En WhatsApp: Más → Exportar chat → Sin archivos.</p>
            <button className="button secondary" onClick={() => fileInput.current?.click()} type="button">Elegir archivo</button>
            <input ref={fileInput} hidden accept=".txt,text/plain" type="file" onChange={handleFile} />
          </div>
          <div className="or-divider"><span>o pega el contenido</span></div>
          <textarea
            className="source-textarea"
            value={sourceText}
            onChange={(event) => setSourceText(event.target.value)}
            placeholder="[25-09-26, 14:32] Cliente: Necesitamos coordinar una visita…"
          />
          <button className="button soft" disabled={!sourceText.trim()} onClick={() => analyze()} type="button">
            <Icon name="sparkles" size={17} /> Analizar conversación
          </button>
        </div>

        <div className="import-details-card card-panel">
          <div className="panel-title-row"><div><p className="section-kicker">PASO 2</p><h2>Confirma el destino</h2></div></div>
          {!preview ? (
            <div className="friendly-empty"><span><Icon name="chat" size={24} /></span><strong>Aquí aparecerá la vista previa</strong><p>Primero carga un chat para detectar participantes, fechas y mensajes.</p></div>
          ) : (
            <>
              <div className="import-stats">
                <article><strong>{preview.messages.length}</strong><span>mensajes</span></article>
                <article><strong>{preview.participants.length}</strong><span>participantes</span></article>
                <article><strong>{validTimestampCount}</strong><span>fechas válidas</span></article>
              </div>
              {preview.warnings.map((warning) => <div className="notice warning" key={warning}><Icon name="bell" size={17} /><span>{warning}</span></div>)}

              <div className="filter-pills" aria-label="Destino de la importación">
                <button className={mode === "new" ? "active" : ""} onClick={() => chooseMode("new")} type="button">Crear conversación</button>
                <button className={mode === "merge" ? "active" : ""} disabled={!remote || !existingGroupConversations.length} onClick={() => chooseMode("merge")} type="button">Fusionar con grupo vivo</button>
              </div>

              {mode === "merge" ? (
                <>
                  <div className="notice"><Icon name="sparkles" size={17} /><span>Los mensajes se anexarán al grupo seleccionado. AWS comparará su huella y omitirá duplicados antes de reanalizar la conversación.</span></div>
                  <div className="form-grid">
                    <label className="wide"><span>Grupo vivo</span><select value={targetConversationId} onChange={(event) => setTargetConversationId(event.target.value)}><option value="">Selecciona un grupo</option>{existingGroupConversations.map((conversation) => <option key={conversation.id} value={conversation.id}>{conversation.customer} · {conversation.site}</option>)}</select></label>
                    <label className="wide"><span>¿Cuál participante corresponde a tu equipo?</span><select value={draft.myParticipantName} onChange={(event) => setDraft({ ...draft, myParticipantName: event.target.value })}><option value="">Selecciona un participante</option>{participantOptions.map((name) => <option key={name}>{name}</option>)}</select><small>Sus mensajes se marcarán como salientes en la línea de tiempo.</small></label>
                  </div>
                  {targetConversation ? <div className="notice"><Icon name="whatsapp" size={17} /><span>Destino: <strong>{targetConversation.customer}</strong> · {targetConversation.site}. El nombre, responsables y estado actuales se conservarán.</span></div> : null}
                </>
              ) : (
                <div className="form-grid">
                  <label><span>Empresa</span>{remote && organizationName ? <input value={organizationName} readOnly /> : <select value={draft.company} onChange={(event) => setDraft({ ...draft, company: event.target.value })}><option>PGR Seguridad</option><option>Seguridad Smart</option><option>Pocatinta</option><option>Casablock</option><option>Accounta</option></select>}</label>
                  <label><span>Cliente</span><input value={draft.customer} onChange={(event) => setDraft({ ...draft, customer: event.target.value })} placeholder="Carozzi" /></label>
                  <label><span>Instalación</span><input value={draft.site} onChange={(event) => setDraft({ ...draft, site: event.target.value })} placeholder="Concepción" /></label>
                  <label><span>Contacto</span><input value={draft.contactName} onChange={(event) => setDraft({ ...draft, contactName: event.target.value })} placeholder="Nombre del contacto" /></label>
                  <label><span>Teléfono</span><input value={draft.phone} onChange={(event) => setDraft({ ...draft, phone: event.target.value })} placeholder="+56 9…" /></label>
                  <label><span>Responsable</span><input value={draft.assignedTo} onChange={(event) => setDraft({ ...draft, assignedTo: event.target.value })} /></label>
                  <label className="wide"><span>¿Cuál participante eres tú?</span><select value={draft.myParticipantName} onChange={(event) => setDraft({ ...draft, myParticipantName: event.target.value })}><option value="">Selecciona un participante</option>{participantOptions.map((name) => <option key={name}>{name}</option>)}</select><small>Los mensajes de este participante se marcarán como salientes.</small></label>
                </div>
              )}

              <div className="message-preview-list">
                <div className="preview-heading"><strong>Primeros mensajes detectados</strong><span>{preview.fileName}</span></div>
                {sampleMessages.map((message) => (
                  <article key={message.id}><span>{message.sender}</span><p>{message.body || "[Mensaje sin texto]"}</p><time>{message.sentAt}</time></article>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {error ? <div className="form-error import-error">{error}</div> : null}
      <section className="import-footer-card">
        <div><p className="section-kicker">PASO 3</p><h2>{mode === "merge" ? "Fusiona el historial con el grupo" : "Revisa y crea la conversación"}</h2><p>{mode === "merge" ? "Los datos actuales del grupo se conservarán; sólo se agregarán mensajes históricos faltantes y se solicitará un nuevo análisis." : remote ? "Se almacenará en DynamoDB con auditoría de importación." : "Se guardará sólo en este navegador mientras usas la demostración."}</p></div>
        <button className="button primary large" disabled={!preview?.messages.length || busy || (mode === "merge" && !targetConversation)} onClick={() => void finishImport()} type="button">
          {busy ? "Importando…" : mode === "merge" ? "Fusionar historial" : "Crear conversación"}<Icon name="arrow" size={18} />
        </button>
      </section>
    </div>
  );
}
